import { useEffect, useState } from 'react';

const DARK_MODE_QUERY = '(prefers-color-scheme: dark)';

// Returns the prefers-color-scheme MediaQueryList, or null in non-browser contexts
// (SSR/tests) or where matchMedia isn't available.
const getDarkModeMediaQuery = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null;
  }
  return window.matchMedia(DARK_MODE_QUERY);
};

// This app has no in-app theme toggle or stored user preference - "the user's theme" is the
// OS/browser prefers-color-scheme setting. This hook makes it reactive so components re-render
// if the OS theme changes while the page is open. Falls back to addListener/removeListener on
// older browsers (Safari < 14) that don't implement the EventTarget interface on
// MediaQueryList.
const useIsDarkTheme = () => {
  const [isDarkTheme, setIsDarkTheme] = useState(() => getDarkModeMediaQuery()?.matches ?? false);

  useEffect(() => {
    const mediaQueryList = getDarkModeMediaQuery();
    if (!mediaQueryList) return undefined;

    const handleChange = (event) => setIsDarkTheme(event.matches);

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleChange);
      return () => mediaQueryList.removeEventListener('change', handleChange);
    }

    mediaQueryList.addListener(handleChange);
    return () => mediaQueryList.removeListener(handleChange);
  }, []);

  return isDarkTheme;
};

export default useIsDarkTheme;
